import React, { useState, useEffect, useMemo } from "react"
import { articlesApi, API_HOST } from "../../services/api"
import { Link, navigate } from "gatsby"
import "./style.css"

function imgSrc(path) {
  if (!path) return null
  if (path.startsWith("http")) return path
  return `${API_HOST}${path}`
}

const getServiceTypeText = (service, lang = "en") => {
  const type = service?.service_type

  if (!type) return ""

  if (typeof type === "object") {
    return lang === "ar"
      ? type.name_ar || type.name_en || type.key || ""
      : type.name_en || type.name_ar || type.key || ""
  }

  return String(type)
}

function formatDate(dateStr, lang) {
  if (!dateStr) return ""
  return new Date(dateStr).toLocaleDateString(
    lang === "ar" ? "ar-AE" : "en-GB",
    { day: "numeric", month: "long", year: "numeric" }
  )
}

function richTextValue(val) {
  if (val == null) return ""
  if (typeof val === "string") return val
  if (typeof val === "object") {
    if (typeof val.body === "string") return val.body
    if (typeof val.html === "string") return val.html
    if (typeof val.to_s === "string") return val.to_s
  }
  return String(val)
}

const BlogPage = ({ lang, location }) => {
  const [articles, setArticles] = useState([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState(null)
  const [selectedFull, setSelectedFull] = useState(null)
  const [isOpen, setIsOpen] = useState(false)
  const [filterType, setFilterType] = useState("all")

  const isAr = lang === "ar"

  
  useEffect(() => {
    if (location?.state?.selectedServiceType) {
      setFilterType(location.state.selectedServiceType)
    }
  }, [location])

  useEffect(() => {
    let cancelled = false
    articlesApi.index()
      .then(data => { 
        if (!cancelled) { 
          setArticles(data)
          setLoading(false) 
        } 
      })
      .catch(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [])

  const openArticle = async (article) => {
    setSelected(article)
    setSelectedFull(null)
    document.body.style.overflow = "hidden"
    
    try {
      const full = await articlesApi.show(article.id)
      setSelectedFull(full)
    } catch (e) {
      console.log("Failed to fetch full article:", e)
    }
    
    requestAnimationFrame(() => requestAnimationFrame(() => setIsOpen(true)))
  }

  const closeArticle = () => {
    setIsOpen(false)
    setTimeout(() => {
      setSelected(null)
      setSelectedFull(null)
      document.body.style.overflow = "unset"
    }, 350)
  }

  useEffect(() => {
    const onEsc = (e) => { if (e.key === "Escape" && selected) closeArticle() }
    window.addEventListener("keydown", onEsc)
    return () => window.removeEventListener("keydown", onEsc)
  }, [selected])

  const title = (a) => isAr ? a.title_ar : a.title_en
  
  const body = () => {
    if (!selected) return ""
    if (selectedFull) {
      const html = isAr ? richTextValue(selectedFull.body_en) : richTextValue(selectedFull.body_ar)
      return html
    }
    return isAr ? selected.body_en_plain : selected.body_ar_plain
  }

  
  const filteredArticles = filterType === "all" 
    ? articles 
    : articles.filter(a => {
        if (!a.services) return false
        return a.services.some(s => 
          getServiceTypeText(s, lang).toLowerCase().replace(/[-_]/g, " ").trim() === 
          filterType.toLowerCase().replace(/[-_]/g, " ").trim()
        )
      })

  
  const serviceTypes = useMemo(() => {
    const types = new Set()
    articles.forEach(a => {
      if (a.services) {
        a.services.forEach(s => {
          const serviceType = getServiceTypeText(s, lang)
          if (serviceType) types.add(serviceType)
        })
      }
    })
    return ["all", ...Array.from(types)]
  }, [articles, lang])

  if (loading) return (
    <div style={{ textAlign: "center", padding: "80px 0", color: "#999" }}>
      {isAr ? "جارٍ التحميل..." : "Loading..."}
    </div>
  )

  return (
    <div
      className={`b-blog-page ${isAr ? "blog-ar-font" : ""}`}
      dir={isAr ? "rtl" : "ltr"}
    >
      <div className="container">
        <div className="blog-page-header">
          <h1 className="blog-page-title">
            {isAr ? "جميع المقالات" : "Blogs"}
          </h1>
        </div>

        <div className="blog-page-filters">
          <div className="blog-page-filter-list">
            {serviceTypes.map(type => (
              <button
                key={type}
                type="button"
                className={`blog-page-filter-btn ${filterType === type ? "active" : ""}`}
                onClick={() => setFilterType(type)}
              >
                {type === "all" 
                  ? (isAr ? "الكل" : "All") 
                  : type.charAt(0).toUpperCase() + type.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {filteredArticles.length === 0 ? (
          <p style={{ textAlign: "center", color: "#999", padding: "60px 0" }}>
            {isAr ? "لا توجد مقالات بعد" : "No articles yet"}
          </p>
        ) : (
          <div className="b-blog-grid">
            {filteredArticles.map((article, index) => (
              <div
                key={article.id}
                className="b-blog-card"
                onClick={() => openArticle(article)}
                role="button"
                tabIndex={0}
                onKeyPress={(e) => e.key === "Enter" && openArticle(article)}
                style={{ animationDelay: `${index * 80}ms` }}
              >
                <div className="b-blog-card-image">
                  {article.image_urls?.[0] ? (
                    <img
                      src={imgSrc(article.image_urls[0])}
                      alt={title(article)}
                      loading="lazy"
                    />
                  ) : (
                    <div className="b-blog-card-placeholder" />
                  )}
                </div>

                <div className="b-blog-card-content">
                  {article.services?.length > 0 && (
                    <div className="b-blog-card-tags">
                      {article.services.slice(0, 2).map(s => (
                        <span key={s.id} className="b-blog-card-tag">
                          {s.service_type}
                        </span>
                      ))}
                    </div>
                  )}

                  <h3 className="b-blog-card-title">{title(article)}</h3>
                  <p className="b-blog-card-date">
                    {formatDate(article.created_at, lang)}
                  </p>
                  
                  <button className="b-blog-card-quick-view">
                    {isAr ? "عرض سريع" : "Quick View"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {selected && (
        <div
          className={`b-blog-modal-overlay ${isOpen ? "open" : "close"}`}
          onClick={closeArticle}
        >
          <div
            className={`b-blog-modal ${isOpen ? "open" : "close"}`}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="b-blog-modal-close"
              onClick={closeArticle}
              aria-label="Close"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2.5">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>

            {selected.image_urls?.[0] && (
              <div className="b-blog-modal-image">
                <img src={imgSrc(selected.image_urls[0])} alt={title(selected)} />
              </div>
            )}

            <div className="b-blog-modal-body">
              {selected.services?.length > 0 && (
                <div className="b-blog-card-tags" style={{ marginBottom: 16 }}>
                  {selected.services.map(s => (
                    <span key={s.id} className="b-blog-card-tag">{s.service_type}</span>
                  ))}
                </div>
              )}

              <h2 className="b-blog-modal-title">{title(selected)}</h2>
              <p className="b-blog-modal-date">{formatDate(selected.created_at, lang)}</p>

              {body() && (
                <div
                  className="b-blog-modal-content"
                  dangerouslySetInnerHTML={{ __html: body() }}
                />
              )}
              
              <div className="b-blog-modal-actions">
                <button 
                  type="button" 
                  className="b-blog-modal-secondary-btn"
                  onClick={closeArticle}
                >
                  {isAr ? "إغلاق" : "Close"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default BlogPage
